import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Flavor lineup quick "Add" buttons and the build-a-bundle page.
 * Adds go through the same cart events as the theme's product form,
 * so the cart drawer and cart bubble update the usual way.
 */

async function refreshCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    return /** @type {any} */ (cartItems).fetchCartData();
  }
  const response = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } });
  return response.json();
}

/**
 * @param {HTMLElement} source - element that dispatches the cart event
 * @param {string} variantId
 * @param {Record<string, string>} [properties] - line item properties
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
async function addToCart(source, variantId, properties = {}) {
  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', '1');
  for (const [key, value] of Object.entries(properties)) {
    formData.append(`properties[${key}]`, value);
  }

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  const config = fetchConfig('javascript', { body: formData });

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      ...config,
      headers: { ...config.headers, Accept: 'text/html' },
    }).then((res) => res.json());

    const ajaxCart = await refreshCart();
    const didError = Boolean(response.status);

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        didError,
        items: ajaxCart.items,
        source: 'srsly-lineup',
        sourceId: source.id || 'srsly-lineup',
        itemCount: 1,
        sections: didError ? undefined : response.sections,
      },
    });

    return didError ? { ok: false, message: response.message || response.description } : { ok: true };
  } catch (error) {
    deferred.reject(error);
    return { ok: false, message: 'Something went wrong. Please try again.' };
  }
}

/**
 * @param {HTMLButtonElement} button
 * @param {string} label
 */
function flash(button, label) {
  const original = button.dataset.label || button.textContent || '';
  button.dataset.label = original;
  button.textContent = label;
  setTimeout(() => {
    button.textContent = original;
    button.disabled = false;
  }, 1800);
}

document.addEventListener('click', async (event) => {
  const button = /** @type {HTMLElement} */ (event.target).closest('[data-srsly-add]');
  if (!(button instanceof HTMLButtonElement)) return;
  event.preventDefault();
  button.disabled = true;
  const result = await addToCart(button, button.dataset.variantId || '');
  flash(button, result.ok ? 'Added' : 'Sold out');
});

/**
 * Build-a-bundle picker. The size buttons switch between pack products (3-pack, 6-pack).
 * Tapping a flavor card adds one jar; the stepper on a picked card adds or removes more of
 * the same flavor. Picks are listed in the pack box and saved on the order as
 * "Flavor 1".."Flavor N" line item properties.
 */
class SrslyPackBuilder extends HTMLElement {
  /** @type {string[]} picked flavor names, in the order they were picked */
  picks = [];

  connectedCallback() {
    this.size = Number(this.dataset.size) || 3;
    this.variantId = '';
    this.cards = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-pick]')]);
    this.sizeButtons = /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[data-bundle-size]')]);
    this.addEventListener('click', this.#onClick);

    // ?size=6 in the link opens the page on the 6-pack.
    const fromUrl = Number(new URL(window.location.href).searchParams.get('size'));
    const usable = (/** @type {number} */ n) => this.sizeButtons.some((b) => Number(b.dataset.bundleSize) === n && !b.disabled);
    const start = usable(fromUrl) ? fromUrl : usable(this.size) ? this.size : Number(this.sizeButtons.find((b) => !b.disabled)?.dataset.bundleSize) || this.size;
    this.#setSize(start, false);
  }

  /**
   * @param {number} size
   * @param {boolean} [fromClick]
   */
  #setSize(size, fromClick = true) {
    const button = this.sizeButtons.find((b) => Number(b.dataset.bundleSize) === size);
    if (!button || button.disabled) return;
    this.size = size;
    this.dataset.size = String(size);
    this.variantId = button.dataset.variantId || '';
    for (const b of this.sizeButtons) b.setAttribute('aria-checked', String(b === button));

    const set = (/** @type {string} */ selector, /** @type {string} */ text) => {
      const el = /** @type {HTMLElement | null} */ (this.querySelector(selector));
      if (!el) return;
      el.textContent = text;
      el.hidden = !text;
    };
    set('[data-pack-title]', `${size}-pack`);
    set('[data-pack-price]', button.dataset.price || '');
    set('[data-pack-compare]', button.dataset.compare || '');
    set('[data-pack-save]', button.dataset.save || '');

    const message = this.querySelector('[data-pack-message]');
    if (this.picks.length > size) {
      this.picks = this.picks.slice(0, size);
      if (message) message.textContent = `We kept your first ${size} picks.`;
    } else if (message && fromClick) {
      message.textContent = '';
    }

    if (fromClick) {
      const url = new URL(window.location.href);
      url.searchParams.set('size', String(size));
      window.history.replaceState(window.history.state, '', url);
    }
    this.#render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const card = /** @type {HTMLElement | null} */ (target.closest('[data-pick]'));
    const name = card?.dataset.name || '';

    if (target.closest('[data-bundle-size]')) {
      const button = /** @type {HTMLElement} */ (target.closest('[data-bundle-size]'));
      this.#setSize(Number(button.dataset.bundleSize));
    } else if (target.closest('[data-pick-plus]')) {
      this.#add(name);
    } else if (target.closest('[data-pick-minus]')) {
      this.#remove(this.picks.lastIndexOf(name));
    } else if (target.closest('[data-pick-select]')) {
      // First tap picks the flavor; tapping a picked card again adds another if there's room.
      this.#add(name);
    } else if (target.closest('[data-slot-remove]')) {
      const slot = /** @type {HTMLElement} */ (target.closest('[data-slot-remove]'));
      this.#remove(Number(slot.dataset.slotRemove));
    } else if (target.closest('[data-pack-add]')) {
      this.#addToCart(/** @type {HTMLButtonElement} */ (target.closest('[data-pack-add]')));
    }
  };

  /** @param {string} name */
  #add(name) {
    if (!name || this.picks.length >= this.size) return;
    this.picks.push(name);
    this.#render();
  }

  /** @param {number} index */
  #remove(index) {
    if (index < 0) return;
    this.picks.splice(index, 1);
    this.#render();
  }

  #render() {
    const full = this.picks.length >= this.size;
    this.classList.toggle('is-full', full);

    for (const card of this.cards) {
      const name = card.dataset.name || '';
      const count = this.picks.filter((pick) => pick === name).length;
      const selected = count > 0;
      card.classList.toggle('is-selected', selected);
      card.querySelector('[data-pick-select]')?.setAttribute('aria-pressed', String(selected));

      const stepper = /** @type {HTMLElement | null} */ (card.querySelector('[data-pick-stepper]'));
      if (stepper) stepper.hidden = !selected;
      const countEl = card.querySelector('[data-pick-count]');
      if (countEl) countEl.textContent = String(count);
      const badgeCount = card.querySelector('[data-pick-badge-count]');
      if (badgeCount) badgeCount.textContent = count > 1 ? `×${count}` : '';
      const plus = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-pick-plus]'));
      if (plus) plus.disabled = full;
      const cta = card.querySelector('.srsly-pick__cta');
      if (cta && !card.classList.contains('is-sold-out')) {
        cta.textContent = selected ? 'In your pack' : full ? 'Pack is full' : 'Tap to add';
      }
    }

    const slots = this.querySelector('[data-pack-slots]');
    if (slots) {
      slots.innerHTML = '';
      for (let i = 0; i < this.size; i++) {
        const name = this.picks[i];
        const li = document.createElement('li');
        li.className = `srsly-slot${name ? '' : ' is-empty'}`;
        if (name) {
          const card = this.cards.find((c) => c.dataset.name === name);
          const thumb = document.createElement('span');
          thumb.className = 'srsly-slot__thumb';
          if (card?.style.getPropertyValue('--flavor-color')) {
            li.style.setProperty('--flavor-color', card.style.getPropertyValue('--flavor-color'));
          }
          if (card?.dataset.image) {
            const img = document.createElement('img');
            img.src = card.dataset.image;
            img.alt = '';
            thumb.append(img);
          }
          const label = document.createElement('span');
          label.className = 'srsly-slot__name';
          label.textContent = name;
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'srsly-slot__remove';
          remove.dataset.slotRemove = String(i);
          remove.setAttribute('aria-label', `Remove ${name}`);
          remove.textContent = '×';
          li.append(thumb, label, remove);
        } else {
          li.innerHTML = `<span class="srsly-slot__thumb"></span><span class="srsly-slot__name">Flavor ${i + 1}</span>`;
        }
        slots.append(li);
      }
    }

    const progress = this.querySelector('[data-pack-progress]');
    if (progress) progress.textContent = `${this.picks.length} of ${this.size} picked`;

    const addButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-pack-add]'));
    const addLabel = this.querySelector('[data-pack-add-label]');
    if (addButton) addButton.disabled = !full;
    if (addLabel) {
      const left = this.size - this.picks.length;
      addLabel.textContent = full ? 'Add pack to cart' : `Pick ${left} more flavor${left === 1 ? '' : 's'}`;
    }
  }

  /** @param {HTMLButtonElement} button */
  async #addToCart(button) {
    if (this.picks.length < this.size) return;
    const message = this.querySelector('[data-pack-message]');
    /** @type {Record<string, string>} */
    const properties = {};
    this.picks.forEach((name, index) => {
      properties[`Flavor ${index + 1}`] = name;
    });

    button.disabled = true;
    if (message) message.textContent = '';
    const result = await addToCart(this, this.variantId || '', properties);

    if (result.ok) {
      this.picks = [];
      this.#render();
      if (message) message.textContent = 'Added! Build another or head to checkout.';
    } else {
      button.disabled = false;
      if (message) message.textContent = result.message || 'Could not add the pack. Please try again.';
    }
  }
}

if (!customElements.get('srsly-pack-builder')) {
  customElements.define('srsly-pack-builder', SrslyPackBuilder);
}

/*
 * One-row flavor slider (homepage). Arrows page through the cards; the bar below shows where
 * you are. Listeners live on the document so they survive the theme re-rendering the section.
 */
if (!window.srslySlider) {
  window.srslySlider = true;

  /** @param {Element} slider */
  const update = (slider) => {
    const track = slider.querySelector('[data-slider-track]');
    const thumb = /** @type {HTMLElement | null} */ (slider.querySelector('[data-slider-thumb]'));
    if (!(track instanceof HTMLElement)) return;
    const max = track.scrollWidth - track.clientWidth;
    const atStart = track.scrollLeft <= 4;
    const atEnd = track.scrollLeft >= max - 4;
    slider.querySelectorAll('[data-slider-step]').forEach((arrow) => {
      /** @type {HTMLElement} */ (arrow).hidden = Number(/** @type {HTMLElement} */ (arrow).dataset.sliderStep) < 0 ? atStart : atEnd;
    });
    if (thumb) {
      const size = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
      thumb.style.width = `${Math.min(100, size * 100)}%`;
      thumb.style.left = `${max > 0 ? (track.scrollLeft / max) * (1 - size) * 100 : 0}%`;
    }
  };

  const updateAll = () => document.querySelectorAll('[data-srsly-slider]').forEach(update);

  document.addEventListener('click', (event) => {
    const arrow = event.target instanceof Element ? event.target.closest('[data-slider-step]') : null;
    if (!(arrow instanceof HTMLElement)) return;
    const slider = arrow.closest('[data-srsly-slider]');
    const track = slider?.querySelector('[data-slider-track]');
    if (!(track instanceof HTMLElement)) return;
    const first = track.children[0];
    const second = track.children[1];
    // Move by however many whole cards are visible (4 on laptops, 2 on phones).
    const step = first && second ? second.getBoundingClientRect().left - first.getBoundingClientRect().left : track.clientWidth;
    const gap = first && second ? step - first.getBoundingClientRect().width : 0;
    const perPage = Math.max(1, Math.round((track.clientWidth + gap) / step - 0.3));
    track.scrollBy({ left: Number(arrow.dataset.sliderStep) * perPage * step, behavior: 'smooth' });
  });

  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target;
      if (!(track instanceof Element) || !track.matches('[data-slider-track]')) return;
      const slider = track.closest('[data-srsly-slider]');
      if (slider) update(slider);
    },
    { capture: true, passive: true }
  );

  window.addEventListener('resize', updateAll, { passive: true });
  window.addEventListener('load', updateAll);
  document.addEventListener('shopify:section:load', updateAll);
  updateAll();
}
